import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getGraphQLClient, RECORD_LAST_LOCATION } from '../lib/graphql';
import { classifyLocation } from '../lib/lastLocation';

// Records the user's last meaningful (book/translation) location to the server
// so "Jump back in" works across devices. Renders nothing.
//
// Debounced so quick pass-through navigations aren't recorded, and de-duped so
// the same path isn't written twice.
export default function RouteTracker() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const lastRecorded = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const classified = classifyLocation(location.pathname);
    if (!classified) return;

    // Store the query string too, so URL-encoded view state (e.g. the reader's
    // ?fullscreen=1) is restored when the user jumps back in.
    const path = location.pathname + location.search;
    if (lastRecorded.current === path) return;

    const timer = setTimeout(() => {
      lastRecorded.current = path;
      getGraphQLClient()
        .request(RECORD_LAST_LOCATION, { path, kind: classified.kind })
        .catch(() => {
          // Non-critical: silently ignore so navigation is never blocked.
          lastRecorded.current = null;
        });
    }, 1200);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search, isAuthenticated]);

  return null;
}
